import { EventEmitter } from '@angular/core';
import { getBorderColorFromPolygonColor } from '@app/utils/_msf';

declare var ol: any;

export interface SplitPolygonItem {
  id: 'old' | 'new';
  polygonJson: string;
  area: number;
}

export class SplitManager {
  private splitMode = false;
  private splitPoints: any[] = [];
  public originalPolygonFeature: any = null;
  /** Store the original style of the polygon before split for undo restoration */
  private originalPolygonStyle: any = null;
  private splitModifyInteraction: any = null;
  /** Whether a split has been performed (split polygons are on the map) */
  private _hasSplit = false;
  /** Click handler reference for swap interaction */
  private _swapClickHandler: ((evt: any) => void) | null = null;
  /** Colors assigned to split polygons [old, new] */
  private _splitColors: string[] = [];

  /** Labels to display on the split polygons (index 0 → 'old', index 1 → 'new') */
  public labels: (string | null)[] = [null, null];

  constructor(
    private getMap: () => any,
    private getVectorSource: () => any,
    private polygonSplitEmitter: EventEmitter<string[]>,
    private enableEditCallback: () => void,
    private onSplitPolygonsChange?: (items: SplitPolygonItem[]) => void
  ) {}

  get isSplitMode(): boolean {
    return this.splitMode;
  }

  /** Whether the map currently has split polygons */
  get hasSplit(): boolean {
    return this._hasSplit;
  }

  activateSplitMode(drawInteraction: any, modifyInteraction: any): void {
    const map = this.getMap();

    this.splitMode = true;
    this.splitPoints = [];

    if (drawInteraction) {
      map.removeInteraction(drawInteraction);
    }
    if (modifyInteraction) {
      map.removeInteraction(modifyInteraction);
    }

    const splitSource = new ol.source.Vector();
    const splitLayer = new ol.layer.Vector({
      source: splitSource,
      style: (feature: any) => {
        const type = feature.get('type');
        if (type === 'splitLine') {
          return new ol.style.Style({
            stroke: new ol.style.Stroke({
              color: '#ff0000',
              width: 3,
              lineDash: [10, 10]
            })
          });
        }
        return new ol.style.Style({
          image: new ol.style.Circle({
            radius: 8,
            fill: new ol.style.Fill({ color: '#ff0000' }),
            stroke: new ol.style.Stroke({ color: '#ffffff', width: 3 })
          }),
          text: new ol.style.Text({
            text: feature.get('label') || '',
            font: 'bold 14px Arial',
            fill: new ol.style.Fill({ color: '#ffffff' }),
            offsetY: 0
          })
        });
      }
    });

    map.addLayer(splitLayer);

    const clickHandler = (evt: any) => {
      if (!this.splitMode) return;

      const coordinate = evt.coordinate;

      const pointFeature = new ol.Feature({
        geometry: new ol.geom.Point(coordinate),
        type: 'splitPoint',
        label: (this.splitPoints.length + 1).toString()
      });
      splitSource.addFeature(pointFeature);
      this.splitPoints.push(coordinate);

      if (this.splitPoints.length === 2) {
        const lineFeature = new ol.Feature({
          geometry: new ol.geom.LineString(this.splitPoints),
          type: 'splitLine'
        });
        splitSource.addFeature(lineFeature);

        setTimeout(() => {
          this.performSplit();

          map.removeLayer(splitLayer);
          map.un('click', clickHandler);
          this.splitMode = false;
          this.splitPoints = [];
        }, 500);
      }
    };

    map.on('click', clickHandler);

    console.log('Split mode activated: Click on two points to split the polygon');
  }

  deactivateSplitMode(): void {
    this.splitMode = false;
    this.splitPoints = [];
  }

  /**
   * Restore the original polygon before the split was performed (undo split).
   * Returns true if restoration was successful.
   */
  restoreOriginal(): boolean {
    if (!this._hasSplit || !this.originalPolygonFeature) {
      return false;
    }

    const vectorSource = this.getVectorSource();
    const map = this.getMap();

    // Remove split features
    const splitFeatures = vectorSource.getFeatures()
      .filter((f: any) => f.get('isSplitResult') === true);
    splitFeatures.forEach((f: any) => vectorSource.removeFeature(f));

    // Remove swap click handler
    if (this._swapClickHandler) {
      map.un('click', this._swapClickHandler);
      this._swapClickHandler = null;
    }

    // Remove split modify interaction
    if (this.splitModifyInteraction) {
      map.removeInteraction(this.splitModifyInteraction);
      this.splitModifyInteraction = null;
    }

    // Re-add the original polygon feature with its original style
    const restored = this.originalPolygonFeature.clone();
    if (this.originalPolygonStyle) {
      restored.setStyle(this.originalPolygonStyle);
    }
    vectorSource.addFeature(restored);

    // Reset state
    this._hasSplit = false;
    this.originalPolygonFeature = null;
    this.originalPolygonStyle = null;
    this._splitColors = [];

    // Emit empty split polygons
    if (this.onSplitPolygonsChange) {
      this.onSplitPolygonsChange([]);
    }
    this.polygonSplitEmitter.emit([]);

    return true;
  }

  /**
   * Swap the 'old' and 'new' ids/labels on the split polygons.
   * This allows the user to choose which polygon is old and which is new.
   */
  swapSplitIds(): void {
    const vectorSource = this.getVectorSource();
    const splitFeatures = vectorSource.getFeatures()
      .filter((f: any) => f.get('isSplitResult') === true);

    if (splitFeatures.length !== 2) return;

    // Swap splitId
    splitFeatures.forEach((f: any) => {
      const currentId = f.get('splitId');
      const newId: 'old' | 'new' = currentId === 'old' ? 'new' : 'old';
      f.set('splitId', newId);
      f.set('splitLabel', newId === 'old' ? this.labels[0] : this.labels[1]);
    });

    // Swap colors
    if (this._splitColors.length === 2) {
      this._splitColors.reverse();
    }

    // Re-apply styles with swapped colors
    splitFeatures.forEach((f: any) => {
      const idx = f.get('splitId') === 'old' ? 0 : 1;
      const color = this._splitColors[idx] || this._splitColors[0];
      const borderColor = getBorderColorFromPolygonColor(color);
      const label = f.get('splitLabel');

      f.setStyle(new ol.style.Style({
        stroke: new ol.style.Stroke({ color: borderColor, width: 2 }),
        fill: new ol.style.Fill({ color: color }),
        ...(label != null ? {
          text: new ol.style.Text({
            text: label,
            font: 'bold 13px Arial',
            fill: new ol.style.Fill({ color: '#ffffff' }),
            stroke: new ol.style.Stroke({ color: '#333333', width: 3 }),
            overflow: true
          })
        } : {})
      }));
    });

    // Re-emit
    this.saveSplit();
  }

  private performSplit(): void {
    const vectorSource = this.getVectorSource();

    const polygonFeatures = vectorSource.getFeatures()
      .filter((f: any) => f.getGeometry().getType() === 'Polygon'
        && f.get('type') !== 'parentPolygon'
        && f.get('type') !== 'siblingPolygon');

    if (polygonFeatures.length === 0) {
      return;
    }

    const polygonFeature = polygonFeatures[0];
    this.originalPolygonFeature = polygonFeature.clone();
    // Save the original style for undo restoration
    this.originalPolygonStyle = polygonFeature.getStyle() ? polygonFeature.getStyle() : null;

    const geometry = polygonFeature.getGeometry();
    const coords = geometry.getCoordinates()[0];

    const wgs84Coords = coords.map((coord: number[]) => ol.proj.toLonLat(coord));
    const wgs84SplitLine = this.splitPoints.map(coord => ol.proj.toLonLat(coord));

    const [p1, p2] = wgs84SplitLine;

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const length = Math.sqrt(dx * dx + dy * dy);
    const extendFactor = 1000;

    const extendedP1 = [
      p1[0] - (dx / length) * extendFactor,
      p1[1] - (dy / length) * extendFactor
    ];
    const extendedP2 = [
      p2[0] + (dx / length) * extendFactor,
      p2[1] + (dy / length) * extendFactor
    ];

    const intersections: { point: number[], index: number, t: number }[] = [];

    for (let i = 0; i < wgs84Coords.length - 1; i++) {
      const intersection = this.getLineIntersection(
        wgs84Coords[i],
        wgs84Coords[i + 1],
        extendedP1,
        extendedP2
      );

      if (intersection) {
        intersections.push({
          point: intersection.point,
          index: i,
          t: intersection.t
        });
      }
    }

    if (intersections.length !== 2) {
      console.error(`Split line must intersect polygon at exactly 2 points, found ${intersections.length}`);
      return;
    }

    intersections.sort((a, b) => {
      if (a.index !== b.index) return a.index - b.index;
      return a.t - b.t;
    });

    const [int1, int2] = intersections;

    const polygon1: number[][] = [];
    for (let i = 0; i <= int1.index; i++) {
      polygon1.push(wgs84Coords[i]);
    }
    polygon1.push(int1.point);
    polygon1.push(int2.point);
    for (let i = int2.index + 1; i < wgs84Coords.length - 1; i++) {
      polygon1.push(wgs84Coords[i]);
    }
    polygon1.push(polygon1[0]);

    const polygon2: number[][] = [int1.point];
    for (let i = int1.index + 1; i <= int2.index; i++) {
      polygon2.push(wgs84Coords[i]);
    }
    polygon2.push(int2.point);
    polygon2.push(polygon2[0]);

    // --- Apply tolerance: offset shared edge points inward by ~0.1m per polygon ---
    // 0.1m in WGS84 degrees ≈ 0.1 / 111320 ≈ 0.000000898
    const toleranceMetres = 0.1;
    const toleranceDeg = toleranceMetres / 111320;

    // Normal to the split line (perpendicular direction)
    const splitDx = int2.point[0] - int1.point[0];
    const splitDy = int2.point[1] - int1.point[1];
    const splitLen = Math.sqrt(splitDx * splitDx + splitDy * splitDy);
    // Unit normal (rotated 90°)
    const nx = -splitDy / splitLen;
    const ny = splitDx / splitLen;

    // Determine which side each polygon is on by checking its centroid
    const centroid = (poly: number[][]): number[] => {
      let cx = 0, cy = 0;
      const n = poly.length - 1; // exclude closing point
      for (let i = 0; i < n; i++) { cx += poly[i][0]; cy += poly[i][1]; }
      return [cx / n, cy / n];
    };

    const c1 = centroid(polygon1);
    const c2 = centroid(polygon2);

    // Dot product of (centroid - int1) with normal tells us which side each polygon is on
    const dot1 = (c1[0] - int1.point[0]) * nx + (c1[1] - int1.point[1]) * ny;
    const dot2 = (c2[0] - int1.point[0]) * nx + (c2[1] - int1.point[1]) * ny;

    const sign1 = dot1 >= 0 ? 1 : -1;
    const sign2 = dot2 >= 0 ? 1 : -1;

    // Offset shared points (int1 and int2) inward for each polygon
    const offsetSharedPoints = (poly: number[][], sign: number): void => {
      const offX = nx * toleranceDeg * sign;
      const offY = ny * toleranceDeg * sign;
      for (let i = 0; i < poly.length; i++) {
        // Check if this point is one of the intersection points (shared edge)
        if (this._isSamePoint(poly[i], int1.point) || this._isSamePoint(poly[i], int2.point)) {
          poly[i] = [poly[i][0] + offX, poly[i][1] + offY];
        }
      }
    };

    offsetSharedPoints(polygon1, sign1);
    offsetSharedPoints(polygon2, sign2);

    const result = [polygon1, polygon2];

    vectorSource.removeFeature(polygonFeature);

    const colors = Array.from({ length: 2 }, () => {
      const r = Math.floor(Math.random() * 156) + 50;
      const g = Math.floor(Math.random() * 156) + 50;
      const b = Math.floor(Math.random() * 156) + 50;
      return `rgba(${r}, ${g}, ${b}, 0.44)`;
    });

    const splitIds: Array<'old' | 'new'> = ['old', 'new'];

    result.forEach((polyCoords: number[][], index: number) => {
      const transformedCoords = polyCoords.map(coord => ol.proj.fromLonLat(coord));
      const splitId = splitIds[index];
      const label = this.labels[index] ?? null;

      const newPolygon = new ol.Feature({
        geometry: new ol.geom.Polygon([transformedCoords]),
        type: 'polygon',
        isSplitResult: true,
        splitId: splitId,
        splitLabel: label
      });

      const borderColor = getBorderColorFromPolygonColor(colors[index]);

      newPolygon.setStyle(new ol.style.Style({
        stroke: new ol.style.Stroke({ color: borderColor, width: 2 }),
        fill: new ol.style.Fill({ color: colors[index] }),
        ...(label != null ? {
          text: new ol.style.Text({
            text: label,
            font: 'bold 13px Arial',
            fill: new ol.style.Fill({ color: '#ffffff' }),
            stroke: new ol.style.Stroke({ color: '#333333', width: 3 }),
            overflow: true
          })
        } : {})
      }));

      vectorSource.addFeature(newPolygon);
    });

    // Save colors for swap functionality
    this._splitColors = [...colors];
    this._hasSplit = true;

    this.saveSplit();
    this.enableEditCallback();
    this._attachModifyInteraction();
    this._attachSwapClickHandler();
  }

  private saveSplit(): void {
    const vectorSource = this.getVectorSource();

    const splitFeatures = vectorSource.getFeatures()
      .filter((f: any) => f.get('isSplitResult') === true);

    if (splitFeatures.length === 0) {
      return;
    }

    const splitPolygons: string[] = splitFeatures.map((feature: any) => {
      const coords = feature.getGeometry().getCoordinates()[0];

      const firstCoord = coords[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      let realCoords = isProjected
        ? coords.map((coord: [number, number]) => ol.proj.toLonLat(coord))
        : [...coords];

      const first = realCoords[0];
      const last = realCoords[realCoords.length - 1];

      if (first[0] !== last[0] || first[1] !== last[1]) {
        realCoords.push([first[0], first[1]]);
      }

      return `POLYGON ((${realCoords
        .map((coord: [number, number]) => `${coord[0].toFixed(6)} ${coord[1].toFixed(6)}`)
        .join(', ')}))`;
    });

    this.polygonSplitEmitter.emit(splitPolygons);
    this._emitSplitPolygonItems(splitFeatures);
  }

  /** Build and emit SplitPolygonItem[] from the current split features */
  private _emitSplitPolygonItems(splitFeatures: any[]): void {
    if (!this.onSplitPolygonsChange) return;

    const items: SplitPolygonItem[] = splitFeatures.map((feature: any) => {
      const coords = feature.getGeometry().getCoordinates()[0];
      const firstCoord = coords[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      let realCoords = isProjected
        ? coords.map((coord: [number, number]) => ol.proj.toLonLat(coord))
        : [...coords];

      const first = realCoords[0];
      const last = realCoords[realCoords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        realCoords.push([first[0], first[1]]);
      }

      const polygonJson = `POLYGON ((${realCoords
        .map((coord: [number, number]) => `${coord[0].toFixed(6)} ${coord[1].toFixed(6)}`)
        .join(', ')}))`;

      const area = this._computeArea(realCoords);

      return {
        id: feature.get('splitId') ?? 'old',
        polygonJson,
        area
      } as SplitPolygonItem;
    });

    this.onSplitPolygonsChange(items);
  }

  /** Compute area in hectares from WGS84 coords */
  private _computeArea(wgs84Coords: number[][]): number {
    try {
      let area = 0;
      const n = wgs84Coords.length - 1;
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += wgs84Coords[i][0] * wgs84Coords[j][1];
        area -= wgs84Coords[j][0] * wgs84Coords[i][1];
      }
      area = Math.abs(area) / 2;
      return Number(((area * 111320 * 111320) / 10000).toFixed(2));
    } catch {
      return 0;
    }
  }

  /**
   * Attach a click handler that allows the user to click on a split polygon
   * to mark it as 'old'. The other polygon automatically becomes 'new'.
   */
  private _attachSwapClickHandler(): void {
    const map = this.getMap();

    // Remove previous handler if any
    if (this._swapClickHandler) {
      map.un('click', this._swapClickHandler);
      this._swapClickHandler = null;
    }

    this._swapClickHandler = (evt: any) => {
      const pixel = evt.pixel;
      const clickedFeature = map.forEachFeatureAtPixel(pixel, (feature: any) => {
        if (feature.get('isSplitResult') === true) {
          return feature;
        }
        return null;
      });

      if (!clickedFeature) return;

      const clickedId = clickedFeature.get('splitId');
      // If the clicked polygon is already 'old', no swap needed
      if (clickedId === 'old') return;

      // Swap: clicked becomes 'old', the other becomes 'new'
      this.swapSplitIds();
    };

    map.on('click', this._swapClickHandler);
  }

  /** Attach a modify interaction so that edits to split polygons update the output */
  private _attachModifyInteraction(): void {
    const map = this.getMap();
    const vectorSource = this.getVectorSource();

    // Remove any previously attached modify interaction
    if (this.splitModifyInteraction) {
      map.removeInteraction(this.splitModifyInteraction);
      this.splitModifyInteraction = null;
    }

    this.splitModifyInteraction = new ol.interaction.Modify({ source: vectorSource });

    this.splitModifyInteraction.on('modifyend', () => {
      const splitFeatures = vectorSource.getFeatures()
        .filter((f: any) => f.get('isSplitResult') === true);
      this._emitSplitPolygonItems(splitFeatures);
    });

    map.addInteraction(this.splitModifyInteraction);
  }

  /** Check if two WGS84 points are the same (within floating-point tolerance) */
  private _isSamePoint(a: number[], b: number[]): boolean {
    return Math.abs(a[0] - b[0]) < 1e-12 && Math.abs(a[1] - b[1]) < 1e-12;
  }

  private getLineIntersection(
    p1: number[], p2: number[],
    p3: number[], p4: number[]
  ): { point: number[], t: number } | null {
    const x1 = p1[0], y1 = p1[1];
    const x2 = p2[0], y2 = p2[1];
    const x3 = p3[0], y3 = p3[1];
    const x4 = p4[0], y4 = p4[1];

    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

    if (Math.abs(denom) < 0.000001) {
      return null;
    }

    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        point: [
          x1 + t * (x2 - x1),
          y1 + t * (y2 - y1)
        ],
        t: t
      };
    }

    return null;
  }
}

